import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, ChevronDown, ChevronUp, Trash2, AlertCircle } from 'lucide-react';
import { userService } from '../../../_services';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';

function ReferralLinksDialog({ open, onOpenChange, title, repid }) {
    const [data, setData] = useState({
        parents: [],
        children: []
    });
    const [loading, setLoading] = useState(true);
    const [emptyStates, setEmptyStates] = useState({
        parents: false,
        children: false
    });
    const [expandedRows, setExpandedRows] = useState({});
    const [deleteDialog, setDeleteDialog] = useState({
        open: false,
        title: '',
        userId: '',
        userType: '',
        groupNumber: '',
        isFullDelete: false
    });
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        if (open && repid) {
            fetchParentChildData();
        }
    }, [open, repid]);

    const fetchParentChildData = async () => {
        setLoading(true);
        setErrorMessage('');
        
        const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
        const [UserId, UserName] = Usermeta_Data.split(':');

        try {
            const apidata = await userService.GetParentChild(UserId, UserName, repid);
            
            const parents = apidata.filter(item => item.usertype?.match("Parent")) || [];
            const children = apidata.filter(item => item.usertype?.match("Children")) || [];

            setData({ parents, children });
            setEmptyStates({
                parents: parents.length === 0,
                children: children.length === 0
            });
        } catch (error) {
            console.error('Error fetching parent/child data:', error);
            setErrorMessage('Error loading referral data');
        } finally {
            setLoading(false);
        }
    };

    const toggleRow = (userId) => {
        setExpandedRows(prev => ({
            ...prev,
            [userId]: !prev[userId]
        }));
    };

    const handleDeleteClick = (userId, userType, groupNumber, isFullDelete) => {
        setDeleteDialog({
            open: true,
            title: isFullDelete ? 'Delete Referral' : 'Delete Group Number Referral',
            userId,
            userType,
            groupNumber,
            isFullDelete
        });
        setErrorMessage('');
    };

    const handleConfirmDelete = async () => {
        const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
        const [UserId, UserName] = Usermeta_Data.split(':');

        try {
            const apidata = await userService.DeactivateParentChild(
                UserId,
                UserName,
                repid,
                deleteDialog.userId,
                deleteDialog.userType,
                deleteDialog.groupNumber
            );

            if (apidata && apidata.length > 0) {
                if (apidata[0].code === '1') {
                    setDeleteDialog({ ...deleteDialog, open: false });
                    fetchParentChildData();
                } else {
                    setErrorMessage('Please try again.');
                }
            }
        } catch (error) {
            console.error('Error deleting referral:', error);
            setErrorMessage('Error deleting referral');
        }
    };

    const renderReferralSection = (sectionTitle, userData, userType, emptyState) => (
        <Card className="mb-4">
            <CardHeader>
                <CardTitle className="text-lg font-semibold text-primary">{sectionTitle}</CardTitle>
            </CardHeader>
            <CardContent>
                {emptyState ? (
                    <div className="text-center py-8 text-muted-foreground">No Record Found</div>
                ) : (
                    <div className="space-y-2">
                        {userData.map((item, index) => (
                            <div key={index} className="border rounded-lg">
                                <div className="flex items-center justify-between p-4 hover:bg-gray-50">
                                    <div className="grid grid-cols-4 gap-4 flex-1">
                                        <div className="font-medium">{item.username}</div>
                                        <div>{item.fullname}</div>
                                        <div className="text-muted-foreground">{item.groupcount} group(s)</div>
                                        <div className="flex items-center gap-2 justify-end">
                                            <Button
                                                variant="ghost-destructive"
                                                size="sm"
                                                onClick={() => handleDeleteClick(item.userid, userType, 'All', true)}
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => toggleRow(item.userid)}
                                            >
                                                {expandedRows[item.userid] ? (
                                                    <ChevronUp className="h-4 w-4" />
                                                ) : (
                                                    <ChevronDown className="h-4 w-4" />
                                                )}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                                
                                {expandedRows[item.userid] && item.groupNumber && item.groupNumber.length > 0 && (
                                    <div className="border-t bg-gray-50 px-4 py-3">
                                        <div className="space-y-2">
                                            <div className="flex justify-between font-semibold text-sm text-foreground pb-2">
                                                <span>Group Number</span>
                                                <span>Actions</span>
                                            </div>
                                            {item.groupNumber.map((group, gIndex) => (
                                                <div key={gIndex} className="flex justify-between items-center py-2 border-b border-border last:border-0">
                                                    <span className="text-sm">{group.groupNumber}</span>
                                                    <Button
                                                        variant="ghost-destructive"
                                                        size="sm"
                                                        onClick={() => handleDeleteClick(item.userid, userType, group.groupNumber, false)}
                                                    >
                                                        <Trash2 className="h-3 w-3" />
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    );

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-semibold text-primary">
                            {title || 'Referral Links'}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="flex-1 overflow-y-auto pr-2">
                        {loading ? (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 className="h-8 w-8 animate-spin text-accent" />
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {renderReferralSection('Parent', data.parents, 'Parent', emptyStates.parents)}
                                {renderReferralSection('Children', data.children, 'Child', emptyStates.children)}
                            </div>
                        )}
                    </div>

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="default"
                            onClick={() => onOpenChange(false)}
                        >
                            Cancel
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <ConfirmDialog
                open={deleteDialog.open}
                onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}
                title={deleteDialog.title}
                description={
                    deleteDialog.isFullDelete
                        ? "Are you sure you want to remove this referral? All group numbers with this relationship will be affected."
                        : "Are you sure you want to remove this referral from this group number?"
                }
                onConfirm={handleConfirmDelete}
                confirmText="Yes"
                cancelText="No"
                variant="destructive"
            >
                {errorMessage && (
                    <Alert variant="destructive" className="mt-4">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{errorMessage}</AlertDescription>
                    </Alert>
                )}
            </ConfirmDialog>
        </>
    );
}

export default ReferralLinksDialog;


